import React from 'react';
import { Document, Page } from '@react-pdf/renderer';
import Header from 'Views/Pages/Pdf/Common/Header';
import {
	CourseEntity, IGeneratedTermEntityAttributes, StudyPeriodEntity, StudyPlanEntity, TermEntity,
} from 'Models/Entities';
import Footer from 'Views/Pages/Pdf/Common/Footer';
import StructureUnitSelectedSummary from 'Views/Pages/Pdf/Common/StructureUnitSelectedSummary';
import PdfBreak from 'Views/Pages/Pdf/Components/PDFBreak';
import UnitCompleted from 'Views/Pages/Pdf/Common/UnitCompleted';
import { RequirementNode } from 'Views/Pages/Frontend/StudyPlanWizard/RequirementTreeGeneration';
import { DataIndex } from 'Util/DataIndexUtils';
import {
	DuplicateCourseItem,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementFulfilmentCheck/DuplicateCourseSelection';
import 'Views/Pages/Pdf/fonts';
import StudyPlanYear from 'Views/Pages/Pdf/StudyPlan/StudyPlanYear';
import { IGeneratedPlan, RequisiteWarning } from 'Queries/Hooks/StudyPlan/types';
import EndOfDocument from 'Views/Pages/Pdf/Common/EndOfDocument';
import { sharedStyles } from 'Views/Pages/Pdf/styles';

interface StudyPlanPdfProps {
	studyPlan: StudyPlanEntity,
	requirementTree: Record<string, RequirementNode>,
	coursesById: DataIndex<CourseEntity>,
	duplicateCourses: DuplicateCourseItem[],
	completedCourseIds: string[],
	getCourseComponents: (courseId: string) => string[];
	allStudyPeriods: StudyPeriodEntity[];
	termsGroupedByYear: DataIndex<IGeneratedTermEntityAttributes[]>;
	currentPlan: IGeneratedPlan;
	allTerms: TermEntity[],
	requisiteWarningsByCourseId: DataIndex<RequisiteWarning[]>,
	totalUnitsSelected: number,
}

const StudyPlanPdf = (props: StudyPlanPdfProps) => {
	const {
		studyPlan,
		requirementTree,
		coursesById,
		duplicateCourses,
		getCourseComponents,
		completedCourseIds,
		allStudyPeriods,
		termsGroupedByYear,
		currentPlan,
		allTerms,
		requisiteWarningsByCourseId,
		totalUnitsSelected,
	} = props;

	return (
		<Document>
			<Page size="A4" style={sharedStyles.body}>
				<Header
					documentName="Study Plan"
					studyPlan={studyPlan}
				/>

				<StructureUnitSelectedSummary
					requirementTree={requirementTree}
					studyPlan={studyPlan}
					coursesById={coursesById}
					duplicateCourses={duplicateCourses}
				/>

				<PdfBreak />

				<UnitCompleted
					studyPlan={studyPlan}
					coursesById={coursesById}
					completedCourseIds={completedCourseIds}
					getCourseComponents={courseId => getCourseComponents(courseId)}
					totalUnitsSelected={totalUnitsSelected}
				/>

				<PdfBreak />

				{Object.keys(termsGroupedByYear).map((year, yearIndex) => (
					<StudyPlanYear
						key={year}
						year={year}
						generatedPlan={currentPlan}
						termsGroupedByYear={termsGroupedByYear}
						allStudyPeriods={allStudyPeriods}
						allTerms={allTerms}
						getCourseComponents={getCourseComponents}
						requisiteWarningsByCourseId={requisiteWarningsByCourseId}
					/>
				))}

				<PdfBreak />

				<EndOfDocument
					customDocumentName="Plan"
				/>

				<Footer studyPlan={studyPlan} />
			</Page>
		</Document>
	);
};

export default StudyPlanPdf;
